/**
 * Canvas 繪圖工具函式
 *
 * 提供常用的繪圖操作：
 * - 基本圖形（線條、矩形、圓形、箭頭）
 * - 文字繪製
 * - 樣式設定
 */

import type { Point, Rectangle } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

/** 線條樣式 */
export interface LineStyle {
  color?: string;
  width?: number;
  dash?: number[];
  cap?: CanvasLineCap;
  join?: CanvasLineJoin;
}

/** 填充樣式 */
export interface FillStyle {
  color?: string;
  opacity?: number;
}

/** 文字樣式 */
export interface TextStyle {
  font?: string;
  size?: number;
  color?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
  bold?: boolean;
}

// ========================================================================
// 基本圖形繪製
// ========================================================================

/**
 * 繪製線條
 */
export function drawLine(
  ctx: CanvasRenderingContext2D,
  start: Point,
  end: Point,
  style: LineStyle = {}
): void {
  const {
    color = '#000000',
    width = 1,
    dash = [],
    cap = 'butt',
    join = 'miter',
  } = style;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = cap;
  ctx.lineJoin = join;
  ctx.setLineDash(dash);

  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();

  ctx.restore();
}

/**
 * 繪製矩形（僅邊框）
 */
export function drawRect(
  ctx: CanvasRenderingContext2D,
  rect: Rectangle,
  style: LineStyle = {}
): void {
  const { color = '#000000', width = 1, dash = [] } = style;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
}

/**
 * 繪製填充矩形
 */
export function fillRect(
  ctx: CanvasRenderingContext2D,
  rect: Rectangle,
  style: FillStyle = {}
): void {
  const { color = '#000000', opacity = 1 } = style;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = color;
  ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
}

/**
 * 繪製圓形（僅邊框）
 */
export function drawCircle(
  ctx: CanvasRenderingContext2D,
  center: Point,
  radius: number,
  style: LineStyle = {}
): void {
  const { color = '#000000', width = 1 } = style;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * 繪製填充圓形
 */
export function fillCircle(
  ctx: CanvasRenderingContext2D,
  center: Point,
  radius: number,
  style: FillStyle = {}
): void {
  const { color = '#000000', opacity = 1 } = style;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * 繪製多邊形（僅邊框）
 */
export function drawPolygon(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  style: LineStyle = {},
  close: boolean = true
): void {
  if (points.length < 2) return;

  const { color = '#000000', width = 1, dash = [] } = style;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);

  ctx.beginPath();
  const first = points[0];
  if (first) {
    ctx.moveTo(first.x, first.y);
  }
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point) {
      ctx.lineTo(point.x, point.y);
    }
  }
  if (close) {
    ctx.closePath();
  }
  ctx.stroke();

  ctx.restore();
}

/**
 * 繪製填充多邊形
 */
export function fillPolygon(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  style: FillStyle = {}
): void {
  if (points.length < 3) return;

  const { color = '#000000', opacity = 1 } = style;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = color;

  ctx.beginPath();
  const first = points[0];
  if (first) {
    ctx.moveTo(first.x, first.y);
  }
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point) {
      ctx.lineTo(point.x, point.y);
    }
  }
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

// ========================================================================
// 進階圖形
// ========================================================================

/**
 * 繪製填充圓角矩形
 */
export function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  rect: Rectangle,
  radius: number,
  style: FillStyle = {}
): void {
  const { x, y, width, height } = rect;
  const { color = '#000000', opacity = 1 } = style;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = color;

  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

// ========================================================================
// 文字繪製
// ========================================================================

/**
 * 繪製文字
 */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  position: Point,
  style: TextStyle = {}
): void {
  const {
    font = 'Arial',
    size = 14,
    color = '#000000',
    align = 'left',
    baseline = 'top',
    bold = false,
  } = style;

  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `${bold ? 'bold ' : ''}${size}px ${font}`;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.fillText(text, position.x, position.y);
  ctx.restore();
}

/**
 * 繪製帶背景的文字標籤
 */
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  position: Point,
  options: {
    textStyle?: TextStyle;
    backgroundColor?: string;
    padding?: number;
    borderRadius?: number;
  } = {}
): void {
  const {
    textStyle = {},
    backgroundColor = 'rgba(255, 255, 255, 0.9)',
    padding = 4,
    borderRadius = 4,
  } = options;

  const { size = 14, font = 'Arial', bold = false } = textStyle;

  // 測量文字尺寸
  ctx.save();
  ctx.font = `${bold ? 'bold ' : ''}${size}px ${font}`;
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const textHeight = size;
  ctx.restore();

  // 繪製背景
  const bgRect: Rectangle = {
    x: position.x - padding,
    y: position.y - padding,
    width: textWidth + padding * 2,
    height: textHeight + padding * 2,
  };

  fillRoundedRect(ctx, bgRect, borderRadius, { color: backgroundColor });

  // 繪製文字
  drawText(ctx, text, position, textStyle);
}

